import { AlertTriangle, Building2, ChevronDown, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { config } from "@/lib/config";
import { useAuthFetch, useTenantSwitch } from "../../hooks/useAuthFetch";
import { cn } from "../../lib/utils";
import { toast } from "../../utils/toast";
import {
  useCreateOrganization,
  useOnboardingOptions,
} from "../../hooks/api/useOnboarding";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { useUIStore } from "../../stores/ui-store";
import { Button } from "../ui/Button";

interface Tenant {
  id: string;
  slug: string;
  name: string;
  role: string;
  subscriptionTier: string;
  joinedAt: string;
}

interface TenantSwitcherProps {
  className?: string;
  showDropdownArrow?: boolean;
  variant?: "default" | "sidebar";
}

export function TenantSwitcher({
  className,
  variant = "default",
}: TenantSwitcherProps) {
  const authFetch = useAuthFetch();
  const { switchTenant } = useTenantSwitch();
  const { currentTenant, setCurrentTenant, sidebarCollapsed } = useUIStore(
    (state) => ({
      currentTenant: state.currentTenant,
      setCurrentTenant: state.setCurrentTenant,
      sidebarCollapsed: state.sidebarCollapsed,
    }),
  );

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousTenantRef = useRef<string | null>(currentTenant);

  // Update ref when currentTenant changes
  useEffect(() => {
    previousTenantRef.current = currentTenant;
  }, [currentTenant]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load available tenants - using the exact same logic as Header
  useEffect(() => {
    const loadTenants = async () => {
      try {
        setIsLoadingTenants(true);
        const response = await authFetch(`${config.apiBaseUrl}/user/tenants`);

        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);

          // Check if current tenant exists in the list
          const currentTenantExists = data.tenants?.find(
            (t: Tenant) => t.slug === currentTenant,
          );
          if (
            currentTenant &&
            !currentTenantExists &&
            data.tenants?.length > 0
          ) {
            const firstTenant = data.tenants[0];
            setCurrentTenant(firstTenant.slug);

            // Show user-friendly notification about the auto-switch
            toast.success(`Switched to organization: ${firstTenant.name}`);
          }
        } else {
          console.error("Failed to load tenants:", response.status);
        }
      } catch (error) {
        console.error("Error loading tenants:", error);
      } finally {
        setIsLoadingTenants(false);
      }
    };

    // Only load tenants if we have a current tenant (app has initialized)
    if (currentTenant) {
      loadTenants();
    }
  }, [authFetch, currentTenant, setCurrentTenant]);

  // Handle tenant switch - using the exact same logic as Header
  const handleTenantSwitch = async (value: string | string[]) => {
    const tenantSlug = Array.isArray(value) ? value[0] : value;
    if (!tenantSlug || tenantSlug === currentTenant) return;

    const selectedTenant = tenants.find((t) => t.slug === tenantSlug);
    if (!selectedTenant) return;

    // Store previous value for potential revert
    const previousTenant = previousTenantRef.current;

    try {
      // Optimistically update the UI first
      setCurrentTenant(tenantSlug);

      // Use the new tenant switch hook
      const result = await switchTenant(tenantSlug);

      if (!result.success) {
        // Revert on error
        setCurrentTenant(previousTenant);
        console.error("Failed to switch tenant:", result.error);
      }
    } catch (error) {
      // Revert on error
      setCurrentTenant(previousTenant);
      console.error("Error switching tenant:", error);
    }
  };

  // Check if we're in collapsed sidebar mode
  const isCollapsed = variant === "sidebar" && sidebarCollapsed;

  if (isLoadingTenants && tenants.length === 0) {
    return null;
  }

  // Show warning if no tenant is selected
  if (!currentTenant) {
    if (isCollapsed) {
      return (
        <div
          className={cn(
            "flex items-center justify-center w-12 h-12 bg-warning-light rounded-lg",
            className,
          )}
        >
          <AlertTriangle className="w-4 h-4 text-warning" />
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-1 bg-warning-light rounded-lg",
          className,
        )}
      >
        <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
        <span className="text-sm text-warning font-medium">
          No organization selected
        </span>
      </div>
    );
  }

  const handleCreateOrganization = () => {
    setShowDropdown(false);
    setShowCreateModal(true);
  };

  // Organization creation modal component
  const CreateOrganizationModal = () => {
    const { data: options, isLoading: optionsLoading } = useOnboardingOptions();
    const createOrganization = useCreateOrganization();

    const [formData, setFormData] = useState({
      organizationName: "",
      organizationSlug: "",
      name: options ? options.user.name : "",
    });

    const [errors, setErrors] = useState<{
      organizationName?: string;
      organizationSlug?: string;
    }>({});

    // Initialize form with suggested values when options load
    useEffect(() => {
      if (options && !formData.organizationName) {
        setFormData({
          organizationName: options.suggestedName,
          organizationSlug: options.suggestedSlug,
          name: options.user.name,
        });
      }
    }, [options, formData.organizationName]);

    const formatSlug = (value: string) => {
      return value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    };

    const handleSlugChange = (value: string) => {
      const formattedSlug = formatSlug(value);
      setFormData((prev) => ({ ...prev, organizationSlug: formattedSlug }));
    };

    const handleOrganizationNameChange = (value: string) => {
      setFormData((prev) => ({
        ...prev,
        organizationName: value,
        organizationSlug:
          prev.organizationSlug === formatSlug(prev.organizationName) ||
          !prev.organizationSlug
            ? formatSlug(value)
            : prev.organizationSlug,
      }));
    };

    const validateForm = () => {
      const newErrors: {
        organizationName?: string;
        organizationSlug?: string;
      } = {};

      if (!formData.organizationName.trim()) {
        newErrors.organizationName = "Organization name is required";
      } else if (formData.organizationName.length < 2) {
        newErrors.organizationName =
          "Organization name must be at least 2 characters";
      }

      if (!formData.organizationSlug.trim()) {
        newErrors.organizationSlug = "Organization slug is required";
      } else if (formData.organizationSlug.length < 2) {
        newErrors.organizationSlug =
          "Organization slug must be at least 2 characters";
      } else if (!/^[a-z0-9-]+$/.test(formData.organizationSlug)) {
        newErrors.organizationSlug =
          "Organization slug can only contain lowercase letters, numbers, and hyphens";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      try {
        const result = await createOrganization.mutateAsync(formData);

        // Store the new tenant in localStorage and set as current
        localStorage.setItem(
          "calendar_admin_tenant",
          JSON.stringify({
            id: result.tenant.id,
            slug: result.tenant.slug,
            name: result.tenant.name,
          }),
        );

        setCurrentTenant(result.tenant.slug);
        setShowCreateModal(false);

        // Reload tenants to show the new organization
        window.location.reload();
      } catch (error) {
        console.error("Organization creation failed:", error);
      }
    };

    if (!showCreateModal) return null;

    const modalContent = (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-text-primary">
              Create Organization
            </h2>
            <button
              onClick={() => setShowCreateModal(false)}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {optionsLoading ? (
            <div className="p-6 text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-text-secondary">Loading...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label
                  htmlFor="modal-org-name"
                  className="block text-sm font-medium text-text-primary mb-2"
                >
                  Organization Name *
                </label>
                <input
                  id="modal-org-name"
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => handleOrganizationNameChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background text-text-primary ${
                    errors.organizationName ? "border-error" : "border-border"
                  }`}
                  placeholder="Enter your organization name"
                  required
                />
                {errors.organizationName && (
                  <p className="mt-1 text-sm text-error">
                    {errors.organizationName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="modal-org-slug"
                  className="block text-sm font-medium text-text-primary mb-2"
                >
                  Organization URL *
                </label>
                <div className="flex items-center">
                  <span className="text-text-secondary text-sm mr-2">
                    yoursite.com/
                  </span>
                  <input
                    id="modal-org-slug"
                    type="text"
                    value={formData.organizationSlug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background text-text-primary ${
                      errors.organizationSlug ? "border-error" : "border-border"
                    }`}
                    placeholder="organization-slug"
                    required
                  />
                </div>
                {errors.organizationSlug && (
                  <p className="mt-1 text-sm text-error">
                    {errors.organizationSlug}
                  </p>
                )}
                <p className="mt-1 text-xs text-text-secondary">
                  This will be your organization's unique URL identifier
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createOrganization.isPending}
                  className="flex-1"
                >
                  {createOrganization.isPending ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Creating...</span>
                    </div>
                  ) : (
                    "Create Organization"
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    );

    return createPortal(modalContent, document.body);
  };

  const dropdown = () => {
    return (
      <div className="bg-background-secondary border border-border-light border-t-0 rounded-b-lg overflow-y-auto max-h-64">
        {/* Other tenants */}
        {tenants
          .filter((t) => t.slug !== currentTenant)
          .map((tenant) => (
            <button
              key={tenant.id}
              onClick={() => {
                handleTenantSwitch(tenant.slug);
                setShowDropdown(false);
              }}
              className="w-full px-4 py-2.5 text-left hover:bg-background-tertiary transition-colors"
            >
              <div className="text-sm font-medium text-text-primary">
                {tenant.name}
              </div>
            </button>
          ))}

        {/* Create Organization Button */}
        <button
          onClick={handleCreateOrganization}
          className="w-full px-4 py-2.5 text-left hover:bg-background-tertiary transition-colors flex items-center gap-2 border-t border-border-light/30"
        >
          <Plus className="w-4 h-4 text-primary" />
          <div className="text-sm font-medium text-text-primary">
            Create Organization
          </div>
        </button>
      </div>
    );
  };

  // In collapsed mode, show just the building icon
  if (isCollapsed) {
    return (
      <>
        <div className={cn("relative", className)} ref={dropdownRef}>
          <Button
            variant="ghost"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-12 h-12 p-2 justify-center mx-auto hover:bg-background-tertiary"
            title={tenants.find((t) => t.slug === currentTenant)?.name}
          >
            <Building2 className="w-4 h-4" />
          </Button>

          {showDropdown && (
            <div className="absolute left-0 mt-2 w-72 bg-background-secondary rounded-lg shadow-xl border border-border py-2 z-[60]">
              {tenants
                .filter((t) => t.slug !== currentTenant)
                .map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      handleTenantSwitch(tenant.slug);
                      setShowDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-background-tertiary transition-colors"
                  >
                    <div className="font-medium text-text-primary">
                      {tenant.name}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {tenant.slug} • {tenant.role}
                    </div>
                  </button>
                ))}
              <button
                onClick={handleCreateOrganization}
                className="w-full px-4 py-2 text-left hover:bg-background-tertiary transition-colors flex items-center gap-2 border-t border-border-light/30 mt-2"
              >
                <Plus className="w-4 h-4 text-primary" />
                <div className="font-medium text-text-primary">
                  Create Organization
                </div>
              </button>
            </div>
          )}
        </div>
        <CreateOrganizationModal />
      </>
    );
  }

  // In expanded mode, use a custom contained dropdown instead of Select
  return (
    <>
      <div className={cn("w-full", className)} ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 transition-all w-full justify-start min-h-[40px]",
            "hover:bg-background-tertiary text-text-secondary hover:text-text-primary border border-border-light",
            showDropdown
              ? "bg-background-secondary rounded-t-lg border-b-0"
              : "rounded-lg",
          )}
          disabled={isLoadingTenants}
        >
          <Building2 className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0 text-left overflow-hidden">
            <div className="text-sm font-medium text-text-primary whitespace-nowrap text-ellipsis overflow-hidden">
              {tenants.find((t) => t.slug === currentTenant)?.name ||
                "Select organization..."}
              {tenants.find((t) => t.slug === currentTenant)?.role && (
                <span className="text-xs text-text-tertiary ml-2">
                  • {tenants.find((t) => t.slug === currentTenant)?.role}
                </span>
              )}
            </div>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-text-secondary transition-transform flex-shrink-0",
              showDropdown ? "rotate-180" : "",
            )}
          />
        </button>

        <div
          className={cn(
            "transition-all duration-200 overflow-hidden",
            showDropdown ? "max-h-64" : "max-h-0",
          )}
        >
          {dropdown()}
        </div>
      </div>
      <CreateOrganizationModal />
    </>
  );
}
